/**
 * Vertical registry — the single source of truth behind
 * `add-document-vertical-framework`.
 *
 * A "vertical" is one document family Vaulytica knows how to review: one
 * classifier family, one or more playbooks, one gated rule pack under a
 * reserved rule-id prefix, and one scope-of-review statement. This module
 * pins the three machine-checkable halves of that contract:
 *
 * 1. {@link NAMESPACE_OWNERS} — which pack owns each rule-id prefix, so two
 *    packs can never silently claim one namespace (guarded by a test).
 * 2. {@link REGISTERED_ASSERTION_GATES} — the opt-in flags/toggles a pack may
 *    gate its rules behind instead of `applies_to_playbooks`. A non-launch
 *    rule must declare one gate or the other; a gate name absent from this
 *    list fails the guard test.
 * 3. {@link SCOPE_OF_REVIEW} — the "reviewed for X; not reviewed for Y"
 *    statement each regulated pack renders on every report it runs on,
 *    following the presence-only doctrine (never a clean bill of health).
 *
 * The prose companion (the pack contract, the honesty posture, the full
 * namespace table) lives in `docs/verticals.md`.
 */

/**
 * Rule-id prefix → the pack that owns it. Every rule id is `PREFIX-NNN`; the
 * guard test asserts no two owners share a prefix and that every shipped
 * non-launch rule uses a registered prefix. Reserved-but-unused prefixes for
 * the current expansion wave are listed so a new pack cannot collide with a
 * planned one.
 */
export const NAMESPACE_OWNERS: Readonly<Record<string, string>> = {
  // v1/v2 launch contract-lint families.
  STRUCT: "launch",
  FIN: "launch",
  TEMP: "launch",
  OBLI: "launch",
  RISK: "launch",
  CHOICE: "launch",
  TERM: "launch",
  IPDATA: "launch",
  PERS: "launch",
  DARK: "launch",
  PROOF: "launch",
  // Mandated text (add-mandated-text-conformance): ungated, because each rule
  // fires only on a document that carries the prescribed passage.
  TEXT: "launch",
  // v3 regulated / deep packs.
  BAA: "baa",
  DPA: "dpa-gdpr",
  USDPA: "dpa-us-state",
  TRANSFER: "cross-border-transfer",
  ADDENDA: "addenda",
  MSA: "msa-deep",
  NDA: "nda-deep",
  // v4 sub-domain packs (classifier families A–P).
  BNK: "banking-finance",
  COMM: "commercial",
  CON: "construction",
  EMP: "employment",
  EQT: "equity-comp",
  EST: "trust-estate",
  GOV: "government",
  HC: "healthcare",
  INS: "insurance",
  IPL: "ip-licensing",
  MNA: "m-and-a",
  POL: "policy",
  PRV: "privacy",
  RE: "real-estate",
  REG: "regulated",
  SET: "settlement",
  // Reserved for the current expansion wave (docs/verticals.md). Not yet
  // shipped — listed so a pack in progress cannot collide with a planned one.
  FILE: "filing-format-lint",
  CITE: "authority-citation-lint",
  DDL: "deadline-computation",
  PROD: "production-qa",
  PNOT: "privacy-notice",
  // v6 law-practice packs (spec-v46.md). The first families Vaulytica reads
  // that the lawyer, rather than the lawyer's client, is the author of.
  ENG: "law-practice-engagement",
  DISC: "discovery-instruments",
  PLDG: "pleadings",
  // v7 — families the catalog had no type for (spec-v49.md).
  SLA: "service-level-agreement",
  PILOT: "pilot-evaluation-agreement",
  ROOM: "roommate-agreement",
  DUA: "hipaa-data-use-agreement",
  SDA: "software-development-agreement",
  MCA: "merchant-cash-advance",
  SOL: "residential-solar-agreement",
  TRA: "training-repayment-agreement",
  BUY: "buyer-representation-agreement",
  ADV: "advisor-agreement",
  MOU: "memorandum-of-understanding",
  SUP: "support-maintenance-agreement",
  BSA: "buy-sell-agreement",
  OBS: "board-observer-agreement",
  PPA: "commercial-power-purchase-agreement",
  SDP: "student-data-privacy-agreement",
  VEH: "vehicle-purchase-agreement",
  MSO: "management-services-agreement",
  // add-readability-measure — under an asserted readability statute.
  READ: "readability",
  // Presence notes on a document no type matched (spec-v50 §11).
  GEN: "generic-fallback",
} as const;

/**
 * Opt-in assertion gates a non-launch rule may declare instead of
 * `applies_to_playbooks` — the user must assert the named flag/toggle for the
 * rule to run. Empty until the first assertion-gated pack ships (e.g.
 * deadline-computation profiles or `--estate-checks`); a rule naming a gate
 * absent from this list fails the guard test.
 */
export const REGISTERED_ASSERTION_GATES: readonly string[] = [
  // add-estate-planning-pack — the estate deepening rules (EST-1xx/2xx/3xx)
  // gate on this assertion because the will/trust playbooks already ship;
  // playbook gating alone would change every existing will/trust hash, so the
  // pack runs only when the user asserts `--estate-checks`.
  "estate-checks",
  // add-redaction-integrity-scan — HANDOFF-008 reads an SEC exhibit's
  // omission legend; a document no SEC-exhibit type matched is in scope only
  // when the user asserts `--sec-exhibit`.
  "sec-exhibit",
  // add-readability-measure — READ-001/002 hold a policy to a readability
  // statute; nothing in a document says where it was filed, so the standard
  // applies only when the user asserts it (`--readability fl`).
  "readability",
];

/** A pack's honesty-bounded statement of what it did and did not review. */
export type ScopeStatement = {
  /** Human label for the pack, e.g. "HIPAA Business Associate Agreement". */
  pack: string;
  /** The concrete things the pack's rules check for. */
  reviewed_for: readonly string[];
  /** Material things a reader might assume were checked but were not. */
  not_reviewed_for: readonly string[];
};

/**
 * Scope statements for the shipped regulated packs, keyed by playbook id.
 * Rendered on every report where the pack ran. The DPA and BAA families are
 * the two regulated verticals live today; future packs register here.
 */
export const SCOPE_OF_REVIEW: Readonly<Record<string, ScopeStatement>> = {
  ...scopeForIds(["baa", "baa-subcontractor"], {
    pack: "HIPAA Business Associate Agreement",
    reviewed_for: [
      "presence of the required BAA safeguards, breach-notification, and subcontractor flow-down terms",
      "permitted-use and return-or-destruction language a HIPAA business-associate agreement is expected to carry",
    ],
    not_reviewed_for: [
      "whether the parties are in fact covered entities or business associates",
      "the sufficiency of the safeguards described under the HIPAA Security Rule",
      "any determination of HIPAA compliance",
    ],
  }),
  ...scopeForIds(
    [
      "dpa-controller-processor",
      "dpa-processor-subprocessor",
      "scc-module-2",
      "scc-module-3",
      "dpa-multi-state-us",
      "dpa-ccpa-service-provider",
      "uk-idta-addendum",
    ],
    {
      pack: "Data Processing Agreement",
      reviewed_for: [
        "presence of the GDPR Article 28 processor terms, sub-processor controls, and international-transfer mechanisms",
        "return-or-deletion, audit, and breach-notification language a data-processing agreement is expected to carry",
      ],
      not_reviewed_for: [
        "whether a valid transfer mechanism actually applies to the parties' data flows",
        "the adequacy of the technical and organizational measures described",
        "any determination of GDPR, UK GDPR, or state-privacy-law compliance",
      ],
    },
  ),
  ...scopeForIds(["last-will-and-testament", "revocable-living-trust", "codicil"], {
    pack: "Estate Instrument Checks",
    reviewed_for: [
      "presence of execution-formality recitals (attestation clause, self-proving affidavit, notary block, testator and witness signature blocks) when --estate-checks is asserted",
      "residuary share arithmetic (numeric shares that do not sum to 100%) and fiduciary/survivorship recitals (executor, successor, guardian for minors, survivorship)",
      "when --state is asserted, witness signature blocks against the number of witnesses that state's execution statute expects, with the execution findings worded to that statute (no attesting witnesses for an ordinary signed will in Pennsylvania, notarization in lieu of witnesses in Colorado and North Dakota, Louisiana's notarial testament)",
    ],
    not_reviewed_for: [
      "estate-instrument checks at all unless --estate-checks is asserted",
      "whether the will is validly executed, or that witnesses were competent, disinterested, or present — recitals are checked, not valid execution",
      "state execution formalities beyond the witness count and the notary alternatives above (witness age or interest, holographic-will and electronic-will requirements), and any conclusion that the instrument is effective in any jurisdiction",
    ],
  }),
  ...scopeForIds(["privacy-notice-us", "privacy-notice-gdpr"], {
    pack: "Privacy Notice Content",
    reviewed_for: [
      "presence of the content items the asserted regime(s) enumerate — collection categories, rights disclosures, opt-out/limit mechanisms, retention, sources/recipients, and contact — when a --regime is asserted (CCPA/CPRA, GDPR Art. 13/14, and the Colorado, Virginia, Texas and Oregon statutory notice lists, including the notice text Texas mandates word for word)",
    ],
    not_reviewed_for: [
      "privacy-notice content at all unless a regime is asserted (--regime); which law applies is the attorney's call",
      "the adequacy or accuracy of any disclosure, or whether it matches the business's actual data practices",
      "privacy regimes other than those listed above (the other state privacy acts), and any compliance conclusion",
    ],
  }),
  ...scopeForIds(["appellate-brief", "trial-motion", "petition"], {
    pack: "Litigation Filing Format & Citations",
    reviewed_for: [
      "citation format and internal consistency: malformed citations, orphaned id., dangling supra/short forms, table-of-authorities reconciliation (by authority), and inconsistent short forms",
      "type-volume (word count against the selected court profile's limit) and page limit where measurable — only when a --court profile is selected",
      "presence of the required filing blocks the selected profile lists (certificate of compliance, certificate of service, tables of contents/authorities, caption, signature block)",
    ],
    not_reviewed_for: [
      "whether any cited authority exists, is quoted accurately, or is still good law — a database check the no-server posture excludes; this remains the filer's duty",
      "table-of-authorities page-number accuracy (the flattened text carries no page boundaries; reconciliation is by authority, not page)",
      "filing-format limits (type-volume, pages, required blocks) unless a court profile is selected (--court)",
      "typeface, point size, line spacing, margins, and the substance of the argument",
    ],
  }),
  ...scopeForIds(
    [
      "engagement-letter",
      "contingency-fee-agreement",
      "flat-fee-agreement",
      "joint-representation-waiver",
      "limited-scope-representation",
      "termination-of-representation",
    ],
    {
      pack: "Law-Practice Engagement Documents",
      reviewed_for: [
        "presence of the terms the ABA Model Rules of Professional Conduct contemplate for an engagement — scope, client identity, fee basis and rates, expenses, advance-fee handling, conflicts consent, and termination and file return",
        "the specific elements Model Rule 1.5(c) enumerates for a contingent fee: the percentage method, whether expenses come off before or after the fee, expenses owed if there is no recovery, and the closing statement",
      ],
      not_reviewed_for: [
        "whether any fee is reasonable under Rule 1.5(a), which is a judgment about the matter and the market that no deterministic check can make",
        "whether a conflict exists, whether it is consentable, or whether consent given was informed — the checks read for the disclosure, never for its sufficiency",
        "compliance with any jurisdiction's rules: the ABA Model Rules bind nobody, every state adopts its own version, and several differ materially on fee-agreement writing requirements and advance-fee handling",
        "any conclusion that a lawyer has satisfied or breached a professional duty",
      ],
    },
  ),
  ...scopeForIds(
    [
      "document-requests",
      "interrogatories",
      "requests-for-admission",
      "discovery-responses",
      "privilege-log",
      "rule-26f-report",
      "deposition-notice",
    ],
    {
      pack: "Discovery Instruments",
      reviewed_for: [
        "presence of the formal elements the Federal Rules require of the instrument — response deadlines, numbering, the Rule 34(b)(1)(C) production form, the Rule 33 numerical limit, the Rule 36 deemed-admitted warning, oath and signature blocks, and certificates of service",
        "the Rule 34(b)(2)(B)-(C) response requirements the 2015 amendments added: objections stated with specificity, an express statement of whether responsive material is being withheld, and a stated production completion date",
        "the Rule 26(b)(5)(A) privilege-log elements: an express claim, a description sufficient to assess it, participants, and dates",
      ],
      not_reviewed_for: [
        "whether any request is relevant or proportional, whether any objection is meritorious, or whether any privilege claim is sound — every one of those is a judgment on the facts of the case",
        "whether a production is complete, or whether anything responsive was missed",
        "local rules and standing orders, which routinely modify response formats, privilege-log requirements, and deposition practice, and which govern over the national rule",
        "state-court procedure, which differs from the Federal Rules in every state",
      ],
    },
  ),
  ...scopeForIds(["complaint", "answer"], {
    pack: "Pleadings",
    reviewed_for: [
      "presence of the structural elements the pleading rules require — the Rule 10(a) caption, the Rule 8(a)(1) jurisdictional statement, venue, numbered paragraphs, the Rule 8(a)(3) demand for relief, the Rule 11(a) signature block, and the Rule 38(b) jury demand",
      "for an answer: a paragraph-by-paragraph response, the Rule 8(b)(5) lack-of-knowledge formulation, affirmative defenses, and preservation of the Rule 12(b)(2)-(5) defenses that Rule 12(h)(1) waives if omitted",
    ],
    not_reviewed_for: [
      "whether any claim is plausible, any defense available, or any allegation true — the checks read form, never merit",
      "whether a Rule 9(b) allegation is particular enough, or whether every applicable affirmative defense was identified; the checks confirm the section exists, not that it is complete",
      "whether every paragraph of a complaint was in fact answered — the check reads for a paragraph-by-paragraph structure, not for a paragraph-level reconciliation",
      "local rules and standing orders on caption format, paragraph numbering, jury-demand placement, and page limits, which govern over the national rule",
    ],
  }),
};

/** Scope statement for the active playbook, or `undefined` if none is registered. */
export function scopeForPlaybook(playbookId: string): ScopeStatement | undefined {
  return SCOPE_OF_REVIEW[playbookId];
}

/**
 * The canonical "which rules does this run include" filter — how activating a
 * vertical pack narrows the candidate rules. A rule is included when:
 *
 * - it has no `applies_to_playbooks`, or the active playbook is in that list; AND
 * - it has no `assertion_gate`, or the named assertion is in `assertions`.
 *
 * Selecting a pack's rules *out* here (rather than letting the engine run and
 * skip-log them) is what makes pack addition hash-neutral for documents
 * outside the pack's gate — the property test proves this on every launch
 * golden. Kept generic over the rule shape so the engine's `Rule` type need
 * not be imported here.
 */
export function selectActiveRules<
  R extends { applies_to_playbooks?: readonly string[]; assertion_gate?: string },
>(rules: readonly R[], playbookId: string, assertions: readonly string[] = []): R[] {
  return rules.filter((r) => {
    if (r.applies_to_playbooks && r.applies_to_playbooks.length > 0) {
      if (!r.applies_to_playbooks.includes(playbookId)) return false;
    }
    if (r.assertion_gate !== undefined) {
      if (!assertions.includes(r.assertion_gate)) return false;
    }
    return true;
  });
}

/** The rule-id prefix (the part before the first `-`), e.g. `STRUCT-003` → `STRUCT`. */
export function rulePrefix(ruleId: string): string {
  const i = ruleId.indexOf("-");
  return i > 0 ? ruleId.slice(0, i) : ruleId;
}

/** Helper: attach one statement to several playbook ids. */
function scopeForIds(
  ids: readonly string[],
  statement: ScopeStatement,
): Record<string, ScopeStatement> {
  const out: Record<string, ScopeStatement> = {};
  for (const id of ids) out[id] = statement;
  return out;
}
